import { isPlatformBrowser } from '@angular/common';
import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { TimeUtils } from '@freelancer/time-utils';
import { SpinnerComponent } from '@freelancer/ui/spinner';
import {
  BehaviorSubject,
  combineLatest,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import { filter, scan, skip, take, withLatestFrom } from 'rxjs/operators';
import { InfiniteScrollContainerDirective } from './infinite-scroll-container.directive';
import {
  InfiniteScrollDirection,
  InfiniteScrollThreshold,
} from './infinite-scroll.types';

const PAGE_RESIZE_DEBOUNCE_TIME = 1000;

/**
 * Allows to implement an infinite scrolling pattern
 *
 * Given a `hasMoreItems` boolean, and with an `[flInfiniteScrollContainer]`
 * directive placed onto the container, this emits a new `limit` when new data
 * should be loaded, e.g. when the user is close to reaching the bottom of the
 * list.
 */
@Component({
  standalone: true,
  selector: 'fl-infinite-scroll',
  imports: [SpinnerComponent],
  template: `
    @if (isLoading ?? hasMoreItems) {
      <fl-spinner></fl-spinner>
    }
  `,
  styleUrls: ['./infinite-scroll.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfiniteScrollComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  private el = inject(ElementRef);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private platformId = inject(PLATFORM_ID);
  private containerRef = inject(InfiniteScrollContainerDirective);
  private timeUtils = inject(TimeUtils);

  @HostBinding('attr.has-more-items')
  @Input()
  hasMoreItems: boolean;

  @Input() isLoading?: boolean | null;

  @Input() scrollDirection: InfiniteScrollDirection = 'top-to-bottom';
  /**
   * The scrollable ancestor to observe against. Required for the sentinel to
   * pre-trigger inside an inner scroll container (e.g. fl-scrollable-content):
   * with the default viewport root, the container clips the sentinel before
   * `rootMargin` is applied, so the trigger only fires once the sentinel is
   * actually visible.
   */
  @Input() scrollRoot?: HTMLElement;
  /** The number of items it loads when triggered */
  @Input() pageSize = 10;
  /**
   * Used to determine how close to the end of the container the infinite scroll should be
   * triggered. Set to an appropriate threshold based on how quickly you want new data to be loaded.
   */
  @Input() scrollThreshold: InfiniteScrollThreshold = 'large';

  @Output() limit: EventEmitter<number> = new EventEmitter();

  private isViewInit: boolean;
  private lastHeight: number;
  private intersectionObserver: IntersectionObserver;
  private resizeObserver: ResizeObserver;

  private loadMoreSubject$ = new Subject<void>();
  private isVisibleSubject$ = new BehaviorSubject<boolean>(false);
  private resizeSubject$ = new Subject<number>();
  @RepetitiveSubscription()
  private loadMoreSubscription?: Subscription;
  private isVisibleSubscription?: Subscription;
  private resizeSubscription?: Subscription;

  setup(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.loadMoreSubscription = this.loadMoreSubject$
        .asObservable()
        .pipe(
          withLatestFrom(of(this.hasMoreItems)),
          filter(([, hasMoreItems]) => hasMoreItems),
          scan((acc, curr) => acc + this.pageSize, this.pageSize),
        )
        .subscribe(limit => {
          this.limit.emit(limit);
          this.changeDetectorRef.markForCheck();
        });

      // Detect when the sentinel is less than 2 viewports away from the
      // current scroll position
      this.intersectionObserver = new IntersectionObserver(
        changes => {
          if (changes.some(change => change.isIntersecting)) {
            this.lastHeight = this.containerRef.container.clientHeight;
            this.isVisibleSubject$.next(true);
          } else {
            this.isVisibleSubject$.next(false);
          }
        },
        {
          root: this.scrollRoot ?? null,
          rootMargin: this.getRootMargin(),
        },
      );
      this.intersectionObserver.observe(this.el.nativeElement);

      this.resizeObserver = new ResizeObserver(
        (observerEntries: ResizeObserverEntry[]) => {
          for (const entry of observerEntries) {
            if (entry.target === this.containerRef.container) {
              this.resizeSubject$.next(
                this.containerRef.container.clientHeight,
              );
            }
          }
        },
      );
      this.resizeObserver.observe(this.containerRef.container);
    }

    // Using debounce here to make sure the component is stable.
    const debouncedResize$ = this.resizeSubject$.pipe(
      this.timeUtils.rxDebounceTime(PAGE_RESIZE_DEBOUNCE_TIME),
    );

    // Some of the components (like images) are loaded asynchronously, so we use combineLatest to avoid triggering loadMore prematurely during the initial load.
    this.isVisibleSubscription = combineLatest([
      debouncedResize$.pipe(take(1)),
      this.isVisibleSubject$,
    ]).subscribe(([_, visible]) => {
      if (visible) {
        this.loadMoreSubject$.next();
      }
    });

    // If new items loaded but the list did not grow enough to push the sentinel out of view,
    // load again. Skips the initial render resize so this only fires after if we need to load
    // more items after it.
    this.resizeSubscription = debouncedResize$
      .pipe(skip(1))
      .subscribe(height => {
        if (this.isVisibleSubject$.value && height >= this.lastHeight) {
          this.loadMoreSubject$.next();
          this.lastHeight = height;
        }
      });
  }

  getRootMargin(): string {
    /**
     * Adjust when to trigger the scroll based on the scroll threshold.
     * eg. the 200% allows the observer to trigger once double the container size from the end
     * of the container is already visible.
     *
     * Smaller container sizes should use a smaller scroll threshold (which uses a smaller margin adjust) as a larger
     * margin adjust may make the trigger already visible upon load/too quickly.
     */
    const marginAdjust = { small: '75%', large: '200%', xlarge: '400%' }[
      this.scrollThreshold
    ];

    return this.scrollDirection === 'bottom-to-top'
      ? `${marginAdjust} 0px 0px 0px`
      : // default to top-to-bottom
        `0px 0px ${marginAdjust} 0px`;
  }

  cleanup(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.loadMoreSubscription) {
      this.loadMoreSubscription.unsubscribe();
    }
    this.isVisibleSubscription?.unsubscribe();
    this.resizeSubscription?.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.isViewInit = true;
    if (this.hasMoreItems) {
      this.setup();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isViewInit && 'hasMoreItems' in changes) {
      if (changes.hasMoreItems.currentValue) {
        this.setup();
      } else {
        this.cleanup();
      }
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }
}
